import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Box, List, ListItem, Button, Popover, SwipeableDrawer, IconButton, useMediaQuery, useTheme } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { WorkspacePremium, CurrencyExchange } from "@mui/icons-material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import theme from "../../assets/theme";
import { generateButtonSx } from "../../utils/elementor-style-override";
import { isFieldAgent } from "../../utils/roleDetection";

/** Hidden from agents — admin-facing management screens. */
const ADMIN_ONLY_KEYS = ["reports", "agent_management", "curriculum_management_admin"];

/** Hidden from admins — the agent's own enrollment view. */
const AGENT_ONLY_KEYS = ["curriculum_enrollments"];

const getNavItems = (isAgent) => {
	const items = [
		{ key: "dashboard", label: "Dashboard", icon: DashboardIcon, link: "/dashboard" },
		{
			key: "agent_management",
			label: "Agent Management",
			icon: ManageAccountsIcon,
			link: "/agent-management",
		},
		{
			key: "curriculum_management_admin",
			label: "Curriculum Management",
			icon: MenuBookIcon,
			link: "/curriculum-management",
		},
		{
			key: "curriculum_enrollments",
			label: "Curriculums & Trainings",
			icon: MenuBookIcon,
			link: "/curriculum-trainings",
		},
		{
			key: "clients",
			label: isAgent ? "My Clients" : "Agents Client",
			icon: PeopleIcon,
			link: "/client-list-2",
		},
		{
			key: "network",
			label: isAgent ? "My Network" : "Agents Network",
			icon: AccountTreeIcon,
			link: "/networks",
		},
		{
			key: "overrides",
			label: "Overrides",
			icon: CurrencyExchange,
			link: "/overrides",
		},
		{
			key: "leadership",
			label: "Leadership Bonuses",
			icon: WorkspacePremium,
			link: "/leadership-bonuses",
		},
		{
			key: "commission_simulation",
			label: "Commission Simulator",
			icon: CalculateOutlinedIcon,
			link: "/commission-simulation",
		},
		{ key: "reports", label: "Reports", icon: AssessmentIcon, link: "/reports" },
		{
			key: "support",
			label: isAgent ? "Support" : "Support Management",
			icon: SupportAgentIcon,
			link: "/support",
		},
		{ key: "settings", label: isAgent ? "Profile Page" : "Settings", icon: isAgent ? PersonIcon : SettingsIcon, link: "/settings" },
	];

	return items.filter((item) => {
		if (isAgent && ADMIN_ONLY_KEYS.includes(item.key)) return false;
		if (!isAgent && AGENT_ONLY_KEYS.includes(item.key)) return false;
		return true;
	});
};

const LOGOUT_ITEM = { key: "logout", label: "Logout", icon: LogoutIcon, link: "/logout" };

/** Shared styles for nav buttons */
const navButtonSx = (isActive) => ({
	width: "100%",
	justifyContent: "flex-start",
	textTransform: "none",
	borderRadius: "8px",
	py: 1,
	gap: 2,
	fontSize: "1rem",
	color: isActive ? "#fff" : "primary.dark",
	backgroundColor: isActive ? "#1D54A2 !important" : "transparent !important",
	...generateButtonSx({ bg: "#1D54A2 !important", text: "#fff" }, { bg: "#1D54A2 !important", text: "#fff !important" }),
});

/** Single nav button */
const NavButton = ({ item, isActive, onNavigate }) => {
	const IconComponent = item.icon;

	if (item.key === "logout") {
		return (
			<Button fullWidth sx={navButtonSx(isActive)} onClick={() => onNavigate(item.key)} startIcon={<IconComponent />}>
				{item.label}
			</Button>
		);
	}

	return (
		<Button component="a" href={item.link} fullWidth sx={navButtonSx(isActive)} startIcon={<IconComponent />}>
			{item.label}
		</Button>
	);
};

/** Full nav list — main items + optional logout pinned at bottom */
const NavList = ({ activePage, onNavigate, showLogout = false, sx = {}, NAV_ITEMS }) => (
	<Box sx={{ display: "flex", flexDirection: "column", height: "100%", ...sx }}>
		<List disablePadding>
			{NAV_ITEMS?.map((item) => (
				<ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
					<NavButton item={item} isActive={activePage === item.key} onNavigate={onNavigate} />
				</ListItem>
			))}
		</List>

		{showLogout && (
			<Box sx={{ mt: "auto", pb: 8 }}>
				<NavButton item={LOGOUT_ITEM} isActive={activePage === LOGOUT_ITEM.key} onNavigate={onNavigate} />
			</Box>
		)}
	</Box>
);

const Sidebar = () => {
	const stored = localStorage.getItem("loggedin_data");

	const user = stored ? JSON.parse(stored) : null;
	const roles = Array.isArray(user?.roles) ? user.roles : [];

	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [activePage, setActivePage] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const isAgent = isFieldAgent(roles);
	const NAV_ITEMS = getNavItems(isAgent);

	useEffect(() => {
		const path = window.location.pathname;
		const allItems = [...NAV_ITEMS, LOGOUT_ITEM];
		const sorted = [...allItems].sort((a, b) => b.link.length - a.link.length);
		const match = sorted.find((item) => path.startsWith(item.link));
		if (match) setActivePage(match.key);
	}, []);

	const handleNavigate = async (key, link, openInNewTab) => {
		setActivePage(key);
		setDrawerOpen(false);

		if (key === "logout") {
			try {
				const nonce = window?.ahgProfile?.nonce;

				const response = await fetch(`${window.location.origin}/wp-json/ahg/v1/logout`, {
					method: "POST",
					credentials: "same-origin",
					headers: {
						"Content-Type": "application/json",
						"X-WP-Nonce": nonce,
					},
				});

				if (response.ok) {
					const data = await response.json();
					window.location.href = data.redirect_url || "/";
				} else {
					console.error("Logout failed:", response.statusText);
					window.location.href = "/wp-login.php?action=logout&redirect_to=/";
				}
			} catch (error) {
				console.error("Error during logout:", error);
				window.location.href = "/wp-login.php?action=logout&redirect_to=/";
			}

			return;
		}

		openInNewTab ? window.open(link, "_blank") : (window.location.href = link);
	};

	const popoverOpen = Boolean(anchorEl);

	return (
		<>
			{/* ── Desktop sidebar ── */}
			{!isMobile && (
				<Box
					component="nav"
					sx={{
						height: "100vh",
						left: 0,
						width: 250,
						backgroundColor: "#e8f3fa",
						p: 1.5,
						display: "flex",
						flexDirection: "column",
						zIndex: 1300,
						overflowY: "auto",
					}}
				>
					<NavList activePage={activePage} onNavigate={handleNavigate} showLogout sx={{ flex: 1 }} NAV_ITEMS={NAV_ITEMS} />
				</Box>
			)}

			{/* ── Mobile hamburger ── */}
			{isMobile && (
				<>
					<Box
						sx={{
							width: "100%",
							px: 1.5,
							py: 1,
							display: "flex",
							justifyContent: "flex-start",
						}}
					>
						<IconButton
							onClick={() => setDrawerOpen(true)}
							aria-label="Open navigation menu"
							sx={{
								backgroundColor: "#1A3F7A !important",
								color: "#fff",
								borderRadius: "8px",
								...generateButtonSx(
									{ bg: "#1D54A2 !important", text: "#fff" },
									{ bg: "#1D54A2 !important", text: "#fff !important" }
								),
							}}
						>
							<Menu size={24} />
						</IconButton>
					</Box>

					{/* Popover (unused but preserved) */}
					<Popover
						open={popoverOpen}
						anchorEl={anchorEl}
						onClose={() => setAnchorEl(null)}
						anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					>
						<NavList activePage={activePage} onNavigate={handleNavigate} showLogout sx={{ p: 1 }} />
					</Popover>

					{/* Swipeable drawer */}
					<SwipeableDrawer
						anchor="left"
						open={drawerOpen}
						onClose={() => setDrawerOpen(false)}
						onOpen={() => setDrawerOpen(true)}
						PaperProps={{
							sx: {
								backgroundColor: "#e8f3fa",
								width: 260,
								pt: 6,
								px: 1.5,
								pb: 2,
								display: "flex",
								flexDirection: "column",
							},
						}}
					>
						<NavList activePage={activePage} onNavigate={handleNavigate} showLogout sx={{ flex: 1 }} NAV_ITEMS={NAV_ITEMS} />
					</SwipeableDrawer>
				</>
			)}
		</>
	);
};

export default Sidebar;
